import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { getAttribution } from '../lib/attribution';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, ArrowLeft, Loader2, Check, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      // First-touch acquisition attribution (captured by PageviewTracker
      // when the visitor first landed on the marketing site). Forwards
      // utm_source/medium/campaign/term/content + referrer so Admin >
      // Traffic & Revenue can attribute a future Pro upgrade back to
      // the original channel.
      const attribution = getAttribution();
      const response = await signup(name, email, password, attribution);
      
      // Check if email verification is required
      if (response.requires_verification) {
        setSignupComplete(true);
        toast.success('Account created! Check your email to verify your address.');
      } else if (response.token) {
        // Legacy flow (if email verification is disabled)
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Show email verification pending screen
  if (signupComplete) {
    const handleResend = async () => {
      setResending(true);
      try {
        await authApi.resendVerification(email);
        toast.success('Verification email sent! Check your inbox.');
      } catch (error) {
        toast.error('Failed to resend email. Please try again.');
      } finally {
        setResending(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 py-12" data-testid="signup-complete-page">
        <div className="w-full max-w-md text-center">
          <img src="/logo.png" alt="Fantastic Forms" className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-4">Check your email!</h1>
          <p className="text-zinc-500 mb-6">
            We've sent a verification link to <strong className="text-zinc-900">{email}</strong>. 
            Click the link in the email to verify your account and log in.
          </p>
          <div className="flex items-center justify-center gap-4 mb-8">
            {[
              { name: 'Google', href: 'https://mail.google.com/mail/u/0/#search/from%3Acontact%40fantasticforms.com',
                svg: <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg> },
              { name: 'Outlook', href: 'https://outlook.live.com/mail/0/search?q=from%3Acontact%40fantasticforms.com',
                svg: <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.16.154-.352.232-.578.232h-8.16v-6.08l1.602 1.172c.078.052.17.078.273.078.104 0 .195-.026.274-.078L24 8.54v-1.16zm-.004-1.735L17.32 10.89l-1.296-.96V5.28h7.195c.227 0 .42.074.577.224.16.15.2.354.2.578v.57z" fill="#0072C6"/><path d="M14.4 5.088v7.2l-1.2.84-4.8-3.528V5.088c0-.206.072-.384.216-.535A.724.724 0 019.151 4.5h5.25v.588zM14.4 13.489v3.91H9.15a.726.726 0 01-.535-.215.728.728 0 01-.216-.535v-6.36L14.4 13.49z" fill="#0072C6"/><path d="M9.6 7.2c0-1.326-.468-2.456-1.404-3.39C7.26 2.876 6.127 2.4 4.8 2.4c-1.326 0-2.458.474-3.395 1.41C.468 4.746 0 5.875 0 7.2v4.8c0 1.326.468 2.458 1.405 3.396C2.342 16.332 3.474 16.8 4.8 16.8c1.327 0 2.46-.468 3.396-1.404C9.132 14.458 9.6 13.326 9.6 12V7.2zM7.2 12c0 .66-.236 1.228-.708 1.692A2.306 2.306 0 014.8 14.4a2.31 2.31 0 01-1.692-.708A2.306 2.306 0 012.4 12V7.2c0-.66.236-1.224.708-1.692A2.31 2.31 0 014.8 4.8c.66 0 1.224.236 1.692.708.472.468.708 1.032.708 1.692V12z" fill="#0072C6"/></svg> },
              { name: 'Proton Mail', href: 'https://mail.proton.me/u/0/inbox?search=from%3Acontact%40fantasticforms.com',
                svg: <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M3.5 7.5L12 13l8.5-5.5V6a2 2 0 00-2-2h-13a2 2 0 00-2 2v1.5z" fill="#6D4AFF"/><path d="M3.5 9.5V18a2 2 0 002 2h13a2 2 0 002-2V9.5L12 15 3.5 9.5z" fill="#6D4AFF" opacity="0.7"/></svg> },
              { name: 'Zoho', href: 'https://mail.zoho.com/zm/#search/from%3Acontact%40fantasticforms.com',
                svg: <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M2 4l7.2 16h1.5L4.5 6h15l1.5-2H2z" fill="#F0483E"/><path d="M22 20l-7.2-16h-1.5L19.5 18h-15L3 20h19z" fill="#089949"/></svg> },
              { name: 'Fastmail', href: 'https://www.fastmail.com/mail/search:from=contact@fantasticforms.com',
                svg: <svg viewBox="0 0 24 24" className="w-5 h-5"><rect x="2" y="4" width="20" height="16" rx="2" fill="#304CFD"/><path d="M2 7l10 6 10-6" stroke="white" strokeWidth="1.5" fill="none"/></svg> },
            ].map((provider) => (
              <a
                key={provider.name}
                href={provider.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative w-10 h-10 rounded-lg border border-zinc-200 bg-white flex items-center justify-center hover:border-zinc-400 hover:shadow-sm transition-all"
                data-testid={`email-provider-${provider.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {provider.svg}
                <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-zinc-500 bg-white px-1.5 py-0.5 rounded border border-zinc-100 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {provider.name}
                </span>
              </a>
            ))}
          </div>
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">Didn't receive the email?</p>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend verification email'
              )}
            </Button>
            <Link to="/login">
              <Button variant="ghost" className="w-full">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 py-12" data-testid="signup-page">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        <Card className="border-zinc-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <img src="/logo.png" alt="Fantastic Forms" className="mx-auto w-12 h-12 mb-4" />
            <CardTitle className="text-2xl font-bold">Create your account</CardTitle>
            <CardDescription>Start building fantastic forms for free</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  data-testid="signup-name-input"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Business Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  data-testid="signup-email-input"
                  className="h-11"
                />
                <p className="text-xs text-zinc-500">Free email providers (Gmail, Yahoo, etc.) are not accepted</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    data-testid="signup-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {password && (
                  <div className="mt-2 space-y-1">
                    {passwordRequirements.map((req, i) => (
                      <div 
                        key={i} 
                        className={`flex items-center gap-2 text-xs ${req.met ? 'text-green-600' : 'text-zinc-400'}`}
                      >
                        <Check className={`w-3 h-3 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                        {req.text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full h-11 bg-zinc-900 hover:bg-zinc-800"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </Button>
            </form>
            
            <p className="mt-4 text-xs text-center text-zinc-500">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-zinc-900">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="underline hover:text-zinc-900">Privacy Policy</Link>
            </p>
            
            <div className="mt-6 text-center text-sm text-zinc-500">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-zinc-900 font-medium hover:underline"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
